import { formatDate } from '../banner/utils';
import { getNestedField } from '../utils';

export function calculateZoom(a: number, b: number, x: number): number {
  const intersectionX = (a + b - 2) / (2 * (b - a - 1));
  const y1 = 2 * b * x - 2 * x + 2 - b;
  const y2 = 2 * a * x + a;
  return x > intersectionX ? y1 : y2;
}
export const defaultTransformation = (): ElementTransformations => ({
  transformMatrix: [1, 0, 0, 1, 0, 0],
  width: [0, 0],
  height: [0, 0],
  top: [0, 0],
  left: [0, 0],
  color: [0, 0, 0],
  text: '',
});
export const rotatePoint = (
  point: { x: number; y: number },
  origin: { x: number; y: number },
  angle: number,
) => {
  const radians = (angle * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const x = point.x - origin.x;
  const y = point.y - origin.y;
  const xPrime = x * cos - y * sin;
  const yPrime = x * sin + y * cos;
  return { x: xPrime + origin.x, y: yPrime + origin.y };
};

export const nullTransformation = (): ElementTransformations => ({
  transformMatrix: [0, 0, 0, 0, 0, 0],
  width: [0, 0],
  height: [0, 0],
  top: [0, 0],
  left: [0, 0],
  rotate: 0,
  text: '',
});

export const defaultBannerForm = (): SingleBannerForm => ({
  id: 0,
  model: {
    dynamic: 'AV',
    size: 'A4',
    orientation: '' as any,
    layout: '',
    campaign: '',
  },
  product: {
    type: 'normal',
    code: '',
    primary: 'DESCRIÇÃO PRIMÁRIA',
    secondary: 'DESCRIÇÃO SECUNDÁRIA',
    unit: 'KG',
    duplicity: 'single',
    product: {
      identifier: 0,
      id: 0,
      type: 'normal',
      ean: '0',
      description: '',
      resume: '',
      code: '',
      filial: '',
      price: {
        id: 0,
        regularPrice: 0,
        promotionalPrice: 0,
        promotionalStartDate: null,
        promotionalEndDate: null,
      },
    },
  },
  price: {
    qrcode: 'http://tools.easysync.com.br',
    barcode: '1234567890',
    price: 0,
    promotional: 0,
    min: 0,
    offer: 0,
    hasExpiration: true,
    hasQrcode: true,
    hasBarcode: true,
    expirationStart: formatDate(new Date()),
    expirationEnd: formatDate(new Date()),
    discount: 0,
    installments: 1,
    releases: [
      '',
      'NO CARTÃO - SEM JUROS',
      'DESCONTO PARA PAGAMENTO À VISTA NO DINHEIRO, DÉBITO, CRÉDITO À VISTA E PIX OU CARTÃO ATÉ 3X',
      'Consulte os vendedores. *',
      'COMPRE ON-LINE',
      '10% DE DESCONTO PARA PAGAMENTO À VISTA',
      'LEVE 3 UNIDADES E PAGUE 2',
    ],
  },
  config: {
    signature: '',
    autocompletes: {
      promotion: {
        column: 'promotion',
        value: '',
        limit: 15,
        filter: [],
      },
      type: {
        column: 'type',
        value: '',
        limit: 15,
      },
    },
    filter: {
      page: 1,
      limit: 15,
      filter: [],
      orderBy: [
        {
          column: 'description',
          value: 'ASC',
        },
      ],
    },
    printer: {
      copies: 1,
    },
  },
});

export const defaultBatchBannerForm = (): BannerForm => ({
  batch: {
    availableProducts: [],
    productType: 'normal',
    total: 0,
    pinnedSingleForms: [],
  },
  filters: {
    products: {
      page: 1,
      limit: 20,
      filter: [],
      orderBy: [],
    },
  },
  showSingleFormId: 0,
  singleForms: [],
  defaultSingleForm: defaultBannerForm(),
  autocompletes: {
    promotion: {
      column: 'promotion',
      value: '',
      limit: 15,
      filter: [],
    },
    type: {
      column: 'type',
      value: '',
      limit: 15,
    },
    items: {
      type: [],
      promotion: [],
    },
  },
});

export function extractParams(str: string) {
  const regex = /{([^}]+)}/g;
  const matches = new Set();
  let match;

  while ((match = regex.exec(str)) !== null) {
    matches.add(match[1]);
  }

  return Array.from(matches);
}

export function replaceParams(text: string, form: any) {
  let resultado = text;
  let match;
  const params: any = extractParams(text).reduce((acc: any, acv: any) => {
    return {
      ...acc,
      [acv]: `${getNestedField(form, acv)}`,
    };
  }, {});
  const regex = /{([^}]+)}/g;
  while ((match = regex.exec(text)) !== null) {
    const propriedade = match[1];
    resultado = resultado.replace(
      new RegExp(`{${propriedade}}`, 'g'),
      params[propriedade] ?? `{${propriedade}}`,
    );
  }

  return resultado;
}

export function replaceValues(str: string, values: any) {
  let resultado = str;
  let match;

  const regex = /{([^}]+)}/g;
  while ((match = regex.exec(str)) !== null) {
    const propriedade = match[1];
    resultado = resultado.replace(
      new RegExp(`{${propriedade}}`, 'g'),
      values[propriedade] ?? `{${propriedade}}`,
    );
  }

  return resultado;
}

export const nodeFunctions: Record<string, any> = {
  identity: (value) => value,
  discount: (price: number, discount: number) => {
    return Math.round(Number(price) * (1 - Number(discount) / 100));
  },
  percent: (price: number, promotionalPrice: number) => {
    if (Math.abs(price) < 1e-7) {
      return 0;
    }
    return ((price - promotionalPrice) * 100) / price;
  },
  round: (value: string | number) => {
    return Math.round(+value);
  },
  installment: (price: number, installments: number) => {
    return installments ? Math.floor(price / installments) : price;
  },
  difference: (price: string | number, promotional: string | number) =>
    (Math.round(Math.floor(+price - +promotional)) / 100).toLocaleString(
      'pt-BR',
      { minimumFractionDigits: 2, maximumFractionDigits: 2 },
    ),
  differencePercent: (price: string | number, promotional: string | number) =>
    +price !== 0
      ? `${Math.floor(((+price - +promotional) / +price) * 100)}%`
      : '0%',
  currency: (value: number | string) => {
    const numericValue = parseInt(String(value).replace(/\D/g, '')) || 0;
    const formattedValue = (numericValue / 100).toFixed(2);
    let [reais, centavos] = formattedValue.split('.');
    reais = parseInt(reais).toLocaleString('pt-BR');
    return `${reais},${centavos}`;
  },
};

export const dynamicField = (form, field) => {
  let dynamicFieldValue: string | undefined;
  if ([null, undefined].includes(field)) dynamicFieldValue = field;
  if (['string', 'number'].includes(typeof field))
    dynamicFieldValue = replaceParams(field, form);
  if (typeof field === 'object' && !Array.isArray(field)) {
    const paramValues = field.params.map(
      ({
        type,
        args,
        default: defaultValue,
        function: func = 'identity',
      }: any) => {
        if (func) {
          const arg = (Array.isArray(args) ? args : [args]).map((arg) =>
            getNestedField(form, arg),
          );
          return {
            type,
            value: nodeFunctions[func](...arg) ?? defaultValue,
          };
        }
        return {
          type,
          value: getNestedField(form, args) ?? defaultValue,
        };
      },
    );
    const params = extractParams(field.value).reduce(
      (acc: any, acv: any, i, a) => {
        return {
          ...acc,
          [acv]: paramValues.find(({ type }) => type === acv)?.value,
        };
      },
      {},
    );
    dynamicFieldValue = replaceValues(field.value, params);
  }
  return dynamicFieldValue;
};

export const visibility = (form: SingleBannerForm) => (node: Interface) => {
  const visibyilityProps = node.properties
    ?.filter(({ type }) => type === 'visibility')
    ?.map(({ key, value, inverted }) => ({
      key,
      value: value ?? true,
      inverted,
    }));
  return (
    !visibyilityProps ||
    visibyilityProps.every(({ key, value, inverted = false }) => {
      const result = (Array.isArray(value) ? value : [value]).includes(
        getNestedField(form, key),
      );
      return inverted ? !result : result;
    })
  );
};
